
import { logger } from '@/utils/logger';

/**
 * Make a request to the OpenAI API
 */
export async function makeOpenAIRequest(
  apiKey: string,
  model: string,
  systemPrompt: string,
  userPrompt: string,
  temperature: number = 0.7,
  maxTokens: number = 2000,
  useSchema: boolean = true,
  responseFormat: 'json_object' | 'text' = 'text'
): Promise<string> {
  if (!apiKey) {
    throw new Error('API key is required');
  }

  try {
    const requestBody: any = {
      model,
      messages: [
        {
          role: 'system',
          content: systemPrompt
        },
        {
          role: 'user',
          content: userPrompt
        }
      ],
      temperature,
      max_tokens: maxTokens
    };

    // Use function calling to enforce schema
    if (useSchema) {
      requestBody.tool_choice = "required";
      requestBody.tools = [
        {
          type: "function",
          function: {
            name: "score_article",
            description: "Returns a structured SEO and AI-readiness score report",
            parameters: {
              type: "object",
              properties: {
                overall_score: { 
                  type: "number",
                  description: "Overall score from 0-100"
                },
                ai_optimization_score: { 
                  type: "number",
                  description: "AI optimization score from 0-20 (also included in category_scores)"
                },
                category_scores: {
                  type: "object",
                  properties: {
                    ai_optimization_score: { 
                      type: "number",
                      description: "AI optimization score from 0-20"
                    },
                    content_quality: { 
                      type: "number",
                      description: "Content quality score from 0-20"
                    },
                    EEAT: { 
                      type: "number",
                      description: "Experience, Expertise, Authoritativeness, Trustworthiness score from 0-20"
                    },
                    helpfulness: { 
                      type: "number",
                      description: "Helpfulness score from 0-20"
                    },
                    shareability: { 
                      type: "number",
                      description: "Shareability score from 0-20"
                    }
                  },
                  required: ["ai_optimization_score", "content_quality", "EEAT", "helpfulness", "shareability"]
                },
                actionable_fixes: {
                  type: "array",
                  items: {
                    type: "object",
                    properties: {
                      current_issue: { type: "string" },
                      old_score: { type: "string" },
                      suggested_fix: { type: "string" },
                      rationale: { type: "string" },
                      new_score: { type: "string" }
                    }
                  }
                },
                shareability_scores: {
                  type: "object",
                  properties: {
                    beginner: {
                      type: "object",
                      properties: {
                        score: { type: "number" },
                        explanation: { type: "string" },
                        improvement: { type: "string" }
                      }
                    },
                    intermediate: {
                      type: "object", 
                      properties: {
                        score: { type: "number" },
                        explanation: { type: "string" },
                        improvement: { type: "string" }
                      }
                    },
                    expert: {
                      type: "object",
                      properties: {
                        score: { type: "number" },
                        explanation: { type: "string" },
                        improvement: { type: "string" }
                      }
                    }
                  }
                },
                bonus_section: {
                  type: "object",
                  properties: {
                    expert_insight: {
                      type: "object",
                      properties: {
                        title: { type: "string" },
                        intro: { type: "string" },
                        key_takeaways: { type: "array", items: { type: "string" } },
                        conclusion: { type: "string" }
                      }
                    },
                    unexpected_analogy: {
                      type: "object",
                      properties: {
                        title: { type: "string" },
                        intro: { type: "string" },
                        key_points: { type: "array", items: { type: "string" } },
                        conclusion: { type: "string" }
                      }
                    },
                    challenged_assumption: {
                      type: "object",
                      properties: {
                        title: { type: "string" },
                        intro: { type: "string" },
                        key_reasons: { type: "array", items: { type: "string" } },
                        solution: { type: "string" }
                      }
                    },
                    insider_tip: {
                      type: "object",
                      properties: {
                        title: { type: "string" },
                        intro: { type: "string" },
                        step_by_step: { type: "array", items: { type: "string" } },
                        final_takeaway: { type: "string" }
                      }
                    }
                  }
                }
              },
              required: ["overall_score", "ai_optimization_score", "category_scores"]
            }
          }
        }
      ];
    } else if (responseFormat === 'json_object') {
      // When not using schema but need JSON response format
      requestBody.response_format = { type: "json_object" };
    }

    // Log request details (without the actual prompts for security)
    logger.info('Making OpenAI API request', {
      component: 'openaiApiClient',
      context: {
        model,
        temperature,
        maxTokens,
        systemPromptLength: systemPrompt.length,
        userPromptLength: userPrompt.length,
        useSchema,
        responseFormat
      }
    });

    // Make the API request
    const response = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiKey}`
      },
      body: JSON.stringify(requestBody)
    });

    // If the response is not OK, log the error and throw an exception
    if (!response.ok) {
      const responseText = await response.text();
      
      // Try to parse as JSON if possible
      let errorMessage: string;
      try {
        const errorData = JSON.parse(responseText);
        errorMessage = errorData.error?.message || `API Error: ${response.status} ${response.statusText}`;
      } catch (e) {
        // If not JSON, use the raw text
        errorMessage = `API Error (${response.status}): ${responseText.substring(0, 200)}`;
      }
      
      logger.error('OpenAI API request failed', new Error(errorMessage), {
        component: 'openaiApiClient',
        context: {
          status: response.status,
          statusText: response.statusText,
          responsePreview: responseText.substring(0, 200)
        }
      });
      
      throw new Error(errorMessage);
    }

    // Check for HTML response, which would indicate an error
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('text/html')) {
      const responseText = await response.text();
      // Extract title if available
      const titleMatch = responseText.match(/<title>(.*?)<\/title>/);
      const errorTitle = titleMatch ? titleMatch[1] : 'Unknown Error';
      
      logger.error('OpenAI API returned HTML instead of JSON', new Error(`API Error: ${errorTitle}`), {
        component: 'openaiApiClient',
        context: {
          contentType,
          responsePreview: responseText.substring(0, 500)
        }
      });
      
      throw new Error(`API Error (${response.status}): ${errorTitle}`);
    }

    // Parse the JSON response
    const data = await response.json();
    
    // Handle function calling response
    if (useSchema && data.choices[0]?.message?.tool_calls) {
      const toolCall = data.choices[0].message.tool_calls[0];
      if (toolCall?.function?.arguments) {
        return toolCall.function.arguments;
      }
    }
    
    // Fallback to regular content
    const content = data.choices[0]?.message?.content;
    
    if (!content) {
      throw new Error('No content in API response');
    }
    
    // Return the content
    return content;
  } catch (error) {
    // Handle fetch errors or other unexpected errors
    if (!(error instanceof Error)) {
      throw new Error('Unknown API request error');
    }
    
    // Log and rethrow the error
    logger.error('OpenAI API request failed with exception', error, {
      component: 'openaiApiClient',
      context: {
        model,
        errorMessage: error.message
      }
    });
    
    throw error;
  }
}
